import {
  ApplicationCredentialSchema,
  ApplicationRoleSchema,
  AuthenticationScriptStatusSchema,
  CredentialGeneratedBySchema,
  CookieSchema,
  CredentialDisableReasonSchema,
  CredentialLoginHealthSchema,
  CredentialVerificationSchema,
  KeyValueSchema,
  MfaConfigurationSchema,
  MfaProviderSchema,
} from '@terra/shared-schemas-orchestrator';
import {
  AuthMaterialExpirySchema,
  CachedCredentialArtifactSummarySchema,
} from '@terra/shared-contracts-authenticator';
import { z } from 'zod';
import { ApplicationCredentialAuthPreflightResultsSchema } from '../auth-preflight/auth-preflight.schema';

export const VerificationArtifactSchema =
  CachedCredentialArtifactSummarySchema.extend({
    value: z.string().optional(),
  });

export const ApplicationCredentialVerificationSchema =
  CredentialVerificationSchema.extend({
    authMaterialExpiry: AuthMaterialExpirySchema.nullable().optional(),
    artifacts: z.array(VerificationArtifactSchema).optional(),
  });

const OptionalCredentialSecretSchema = z
  .string()
  .nullable()
  .optional()
  .transform((value) => {
    if (value == null) {
      return value;
    }

    const normalizedValue = value.trim();

    return normalizedValue.length > 0 ? normalizedValue : null;
  });

const BodySignatureAlgorithmSchema = z.enum(['sha1', 'sha256', 'sha512']);

const BodySignatureSchema = z.object({
  headerName: z.string().min(1),
  algorithm: BodySignatureAlgorithmSchema,
  secret: z.string().min(1),
});

export const EnrollmentExtraFieldSchema = z.object({
  label: z.string().min(1),
  value: z.string().min(1),
});

export const EnrollmentExtrasSchema = z.object({
  fields: z.array(EnrollmentExtraFieldSchema).optional().default([]),
});

export const MfaCreateConfigSchema = z.object({
  id: z.string().optional(),
  enabled: z.boolean(),
  providerType: MfaProviderSchema.optional(),
  totpSecret: z.string().optional(),
  phoneNumber: z.string().optional(),
});

export const CredentialBaseSchema = ApplicationCredentialSchema.pick({
  id: true,
  applicationId: true,
  name: true,
  ssoProvider: true,
  role: true,
  roleId: true,
  loginUrl: true,
  username: true,
  password: true,
  tenant: true,
  createdAt: true,
  isDisabled: true,
  disabledAt: true,
  disableReason: true,
}).extend({
  disableReason: CredentialDisableReasonSchema.nullable().optional(),
  authenticationScriptStatus: AuthenticationScriptStatusSchema.optional(),
  authenticationScript: z.string().nullable().optional(),
  cookies: z.array(CookieSchema),
  headers: z.array(KeyValueSchema),
  secrets: z.array(KeyValueSchema),
  verification: ApplicationCredentialVerificationSchema.nullish(),
  loginHealth: CredentialLoginHealthSchema.nullish(),
  authPreflight: ApplicationCredentialAuthPreflightResultsSchema.nullish(),
  mfaConfig: z.array(MfaConfigurationSchema),
  basicAuthUsername: OptionalCredentialSecretSchema,
  basicAuthPassword: OptionalCredentialSecretSchema,
  mtlsCert: OptionalCredentialSecretSchema,
  mtlsKey: OptionalCredentialSecretSchema,
  mtlsPfx: OptionalCredentialSecretSchema,
  mtlsOrigin: OptionalCredentialSecretSchema,
  mtlsPassphrase: OptionalCredentialSecretSchema,
  generatedBy: CredentialGeneratedBySchema.optional(),
  generatedMailboxAddress: z.string().nullable().optional(),
  enrollmentExtras: EnrollmentExtrasSchema.nullish(),
  roleDescription: z.string().nullable().optional(),
  bodySignature: BodySignatureSchema.nullable().optional(),
});

export const CreateCredentialSchema = CredentialBaseSchema.omit({
  applicationId: true,
  generatedBy: true,
  mfaConfig: true,
  createdAt: true,
  isDisabled: true,
  disabledAt: true,
  disableReason: true,
  authenticationScriptStatus: true,
  authenticationScript: true,
  loginHealth: true,
  authPreflight: true,
})
  .extend({
    name: z.string().nullable().optional(),
    mfaConfig: z.array(MfaCreateConfigSchema).optional(),
    username: z.string(),
    role: z.string().min(1, 'Role is required'),
    roleId: z.string().nullable().optional(),
    loginUrl: z
      .union([z.url('Login URL must be a valid URL'), z.literal(''), z.null()])
      .transform((val) => (val === '' ? null : val))
      .optional(),
  })
  .superRefine((credential, context) => {
    const hasAnyMutualTlsField =
      (credential.mtlsCert?.trim().length ?? 0) > 0 ||
      (credential.mtlsKey?.trim().length ?? 0) > 0 ||
      (credential.mtlsPfx?.trim().length ?? 0) > 0 ||
      (credential.mtlsOrigin?.trim().length ?? 0) > 0 ||
      (credential.mtlsPassphrase?.trim().length ?? 0) > 0;

    if (!hasAnyMutualTlsField) {
      return;
    }

    const hasPfx = (credential.mtlsPfx?.trim().length ?? 0) > 0;
    const hasCert = (credential.mtlsCert?.trim().length ?? 0) > 0;
    const hasKey = (credential.mtlsKey?.trim().length ?? 0) > 0;

    if (!hasPfx && !hasCert) {
      context.addIssue({
        code: 'custom',
        message:
          'mTLS certificate or PFX bundle is required when configuring mTLS',
        path: ['mtlsCert'],
      });
    }

    if (!hasPfx && !hasKey) {
      context.addIssue({
        code: 'custom',
        message:
          'mTLS private key is required when configuring mTLS without a PFX bundle',
        path: ['mtlsKey'],
      });
    }

    if ((credential.mtlsOrigin?.trim().length ?? 0) === 0) {
      context.addIssue({
        code: 'custom',
        message: 'mTLS origin is required when configuring mTLS',
        path: ['mtlsOrigin'],
      });
    }
  });

export const CreateCredentialsRequestSchema = z.object({
  credentials: z.array(CreateCredentialSchema),
});

export const CredentialSchema = z.object({
  credential: CredentialBaseSchema.omit({
    verification: true,
    loginHealth: true,
    authPreflight: true,
  }),
});

export const CredentialApiSchema = z.object({
  credential: CredentialBaseSchema.omit({
    applicationId: true,
  }),
});

export const CredentialsApiSchema = z.object({
  credentials: z.array(
    CredentialBaseSchema.omit({ applicationId: true }).extend({
      applicationRole: ApplicationRoleSchema.nullable().optional(),
    }),
  ),
});

export const OtpProviderConfigSchema = z.object({
  totpSecret: z.string(),
});

export const SmsProviderConfigSchema = z.object({
  areaCode: z.string().optional(),
  phoneNumber: z.string().optional(),
});

export const EmailProviderConfigSchema = z.object({
  domain: z.string().optional(),
  preferredEmailAddress: z.string().optional(),
  // Future Email-specific config
});

export const WebhookProviderConfigSchema = z.object({
  // Reserved for future webhook-specific config (e.g. custom header names)
});

export const MfaGenerateOtpSchema = z.object({
  credentialId: z.string(),
  mfaConfigId: z.string(),
  providerType: z.literal(MfaProviderSchema.enum.OTP),
  providerConfig: OtpProviderConfigSchema,
});

export const MfaGenerateSmsSchema = z.object({
  credentialId: z.string(),
  mfaConfigId: z.string(),
  providerType: z.literal(MfaProviderSchema.enum.SMS),
  providerConfig: SmsProviderConfigSchema.optional(),
});

export const MfaGenerateEmailSchema = z.object({
  credentialId: z.string(),
  mfaConfigId: z.string(),
  providerType: z.literal(MfaProviderSchema.enum.EMAIL),
  providerConfig: EmailProviderConfigSchema.optional(),
});

export const MfaGenerateWebhookSchema = z.object({
  credentialId: z.string(),
  mfaConfigId: z.string(),
  providerType: z.literal(MfaProviderSchema.enum.WEBHOOK),
  providerConfig: WebhookProviderConfigSchema.optional(),
});

export const MfaGenerateSchema = z.discriminatedUnion('providerType', [
  MfaGenerateOtpSchema,
  MfaGenerateSmsSchema,
  MfaGenerateEmailSchema,
  MfaGenerateWebhookSchema,
]);

export const MfaSetupOtpSchema = z.object({
  credentialId: z.string(),
  mfaConfigId: z.string(),
  totpSecret: z.string(),
});

export const MfaGetMessagesSchema = z.object({
  credentialId: z.string(),
  mfaConfigId: z.string(),
  waitForFresh: z.boolean().optional(),
  untruncated: z.boolean().optional(),
});

export const MfaGenerateOtpCodeSchema = z.object({
  credentialId: z.string(),
  mfaConfigId: z.string(),
});

export const MfaMessageSchema = z.object({
  id: z.string(),
  content: z.string(),
  receivedAt: z.date(),
  isOtp: z.boolean(),
  from: z.string().optional(),
  expiresInSeconds: z.number().optional(),
  links: z.array(z.string()).optional(),
  rawPlain: z.string().optional(),
  rawHtml: z.string().optional(),
});

export const MfaProviderResponseSchema = z.object({
  success: z.boolean(),
  mfaConfigId: z.string().optional(),
  providerType: MfaProviderSchema.optional(),
  emailAddress: z.string().optional(),
  phoneNumber: z.string().optional(),
  messages: z.array(MfaMessageSchema).optional(),
  otpCode: z.string().optional(),
  error: z.string().optional(),
});

export const CredentialPreviewSchema = z.object({
  id: z.string(),
  username: z.string(),
  role: z.string(),
});

export const CredentialsPreviewSchema = z.object({
  credential: CredentialPreviewSchema.nullable(),
});

export const GenerateCredentialMailboxResponseSchema = z.object({
  success: z.boolean(),
  emailAddress: z.string().optional(),
  error: z.string().optional(),
});

export const CredentialInboxAttachmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  contentType: z.string(),
  size: z.number(),
  contentBase64: z.string().optional(),
});

export const CredentialInboxMessageSchema = z.object({
  id: z.string(),
  content: z.string(),
  subject: z.string().optional(),
  from: z.string().optional(),
  receivedAt: z.string(),
  links: z.array(z.string()).optional(),
  rawPlain: z.string().optional(),
  rawHtml: z.string().optional(),
  attachments: z.array(CredentialInboxAttachmentSchema).optional(),
});

export const CredentialInboxMessagesResponseSchema = z.object({
  success: z.boolean(),
  emailAddress: z.string().optional(),
  messages: z.array(CredentialInboxMessageSchema),
  error: z.string().optional(),
});
